/**
 * repurpose/Source.jsx
 * Choosing what is being repurposed, and getting words out of it.
 *
 * Two ways in, because there are honestly only two: paste something written,
 * or point at a file. A video or a voice note can be transcribed; a picture
 * cannot, and the button says so rather than failing when pressed.
 *
 * The transcript is editable before anything is generated. A mangled product
 * name in a transcript would otherwise propagate into all eight versions, and
 * fixing it afterwards means paying twice.
 *
 * Dependencies: services/api.js, components/Icons.jsx
 */

import { useRef, useState } from 'react';
import { api } from '../../services/api.js';
import { Check, Film, Image as ImageIcon, Sparkle, Upload } from '../Icons.jsx';

const mb = (bytes) => `${Math.round((bytes / (1024 * 1024)) * 10) / 10}MB`;

const duration = (seconds) => {
  if (!seconds) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

export default function Source({
  brandId,
  library,
  limits,
  title,
  onTitle,
  goal,
  onGoal,
  mode,
  onMode,
  mediaId,
  onMedia,
  text,
  onText,
  transcribed,
  onTranscribe,
  transcribing,
  onUploaded,
}) {
  const file = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const chosen = library.find((m) => m.id === mediaId) ?? null;

  async function upload(e) {
    const picked = e.target.files?.[0];
    e.target.value = '';
    if (!picked) return;

    setUploading(true);
    setUploadError(null);
    try {
      const added = await api.media.upload(brandId, picked);
      await onUploaded(added.media?.id ?? added.id);
    } catch (err) {
      setUploadError(err.message ?? 'That would not upload.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <section className="rp-source card">
      <header className="rp-source__head">
        <h2>What are you repurposing?</h2>
        <div className="rp-modes" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'text'}
            className={mode === 'text' ? 'is-on' : ''}
            onClick={() => onMode('text')}
          >
            Paste something
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'media'}
            className={mode === 'media' ? 'is-on' : ''}
            onClick={() => onMode('media')}
          >
            A video or picture
          </button>
        </div>
      </header>

      <div className="rp-field">
        <label htmlFor="rp-title">What is it called?</label>
        <input
          id="rp-title"
          value={title}
          onChange={(e) => onTitle(e.target.value)}
          placeholder="Autumn launch walkthrough"
          maxLength={200}
        />
      </div>

      <div className="rp-field">
        <label htmlFor="rp-goal">What is it for? (optional)</label>
        <input
          id="rp-goal"
          value={goal}
          onChange={(e) => onGoal(e.target.value)}
          placeholder="Get people onto the free trial"
          maxLength={300}
        />
      </div>

      {mode === 'media' && (
        <div className="rp-library">
          <div className="rp-library__head">
            <span className="muted">Choose from the library</span>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => file.current?.click()}
              disabled={uploading}
            >
              <Upload size={14} />
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
            <input
              ref={file}
              type="file"
              hidden
              accept="image/*,video/mp4,video/quicktime,video/x-m4v"
              onChange={upload}
            />
          </div>

          {uploadError && <p className="rp-error">{uploadError}</p>}

          {library.length === 0 ? (
            <p className="muted">Nothing in the library yet. Upload a file to start.</p>
          ) : (
            <ul className="rp-grid">
              {library.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    className={`rp-tile ${m.id === mediaId ? 'is-on' : ''}`}
                    onClick={() => onMedia(m.id)}
                    title={m.originalName || m.id}
                  >
                    {m.kind === 'video' ? (
                      <span className="rp-tile__video">
                        <Film size={20} />
                      </span>
                    ) : (
                      <img src={api.media.rawUrl(m.id)} alt={m.altText || ''} loading="lazy" />
                    )}
                    <span className="rp-tile__meta">
                      {duration(m.durationSeconds) ?? (m.bytes ? mb(m.bytes) : '')}
                    </span>
                    {m.id === mediaId && (
                      <span className="rp-tile__on">
                        <Check size={14} />
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {chosen && (
            <div className="rp-chosen">
              <span className="rp-chosen__name">
                {chosen.kind === 'video' ? <Film size={14} /> : <ImageIcon size={14} />}
                {chosen.originalName || chosen.id}
              </span>
              {chosen.canTranscribe ? (
                <button
                  type="button"
                  className="btn btn--sm"
                  onClick={onTranscribe}
                  disabled={transcribing}
                >
                  <Sparkle size={14} />
                  {transcribing ? 'Listening…' : 'Transcribe it'}
                </button>
              ) : (
                <span className="muted">{chosen.transcribeReason}</span>
              )}
            </div>
          )}
        </div>
      )}

      <div className="rp-field">
        <label htmlFor="rp-text">
          {mode === 'media' ? 'What is in it' : 'The article, script or notes'}
        </label>
        <textarea
          id="rp-text"
          value={text}
          onChange={(e) => onText(e.target.value)}
          rows={mode === 'media' ? 8 : 12}
          maxLength={limits.maxSourceChars}
          placeholder={
            mode === 'media'
              ? 'Transcribe the file, or describe what happens in it.'
              : 'Paste the whole thing. Everything written will come from these words and nothing else.'
          }
        />
        <div className="rp-count">
          {transcribed && (
            <span className="rp-badge">
              <Sparkle size={12} /> Transcribed
            </span>
          )}
          <span className={text.length < limits.minSourceChars ? 'rp-count--short' : 'muted'}>
            {text.length.toLocaleString()} / {limits.maxSourceChars.toLocaleString()}
            {text.length < limits.minSourceChars &&
              ` — at least ${limits.minSourceChars} to work from`}
          </span>
        </div>
      </div>
    </section>
  );
}
